import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { usePreviewWithDataContext } from 'modules/templateEditor/context/PreviewWithDataContext';
import { OSWebPush, TemplateEditViews } from 'modules/templateEditor/common';
import { WebPushContentDefinition } from 'modules/graphql';
import { WindowBattery, WindowWifi } from 'ui/icons';
import { colors } from 'ui/theme';

import { useWebPushPreviewContext } from '../context/WebPushPreviewContext';
import { getDateShort, getTime, truncateText } from '../../common/utils';
import {
  WEB_PUSH_NON_WINDOWS_BODY_LIMIT,
  WEB_PUSH_NON_WINDOWS_TITLE_LIMIT,
  WEB_PUSH_WINDOWS_BODY_LIMIT,
  WEB_PUSH_WINDOWS_TITLE_LIMIT,
} from '../constants';
import {
  messageDesktopBody,
  messageDesktopHeader,
  messageDesktopTime,
  messageDesktopTimeWithPreview,
  messageDesktopContainer,
  messageDesktopNow,
  messageDesktopTitle,
  messageDesktopMedia,
  messageDesktopContent,
} from './styles';

type Props = {
  selectedOS: OSWebPush;
};

export const DesktopPreview = ({ selectedOS }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { locale, view } = useQueryParamContext();
  const { isPreviewWithData } = useWebPushPreviewContext();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  // note: this date and time appear in a localized preview, so we specify the locale of the template
  const dateTime = new Date();
  const time = getTime(dateTime, locale);
  const date = getDateShort(dateTime, locale);

  const { messageContentTitle, messageContentBody } = useWebPushPreviewContext();
  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const webPushData = (liveTemplate?.mediumData || {}) as WebPushContentDefinition;
  const title = usePopulatedField ? messageContentTitle : webPushData.title;
  const body = usePopulatedField ? messageContentBody : webPushData.message;

  const timeStyle = isPreviewWithData ? messageDesktopTimeWithPreview : messageDesktopTime;
  const supportsImage = selectedOS === OSWebPush.Android || selectedOS === OSWebPush.Windows;

  return (
    <>
      <div style={messageDesktopHeader}>
        <WindowWifi color={colors.grey35} height={17} width={17} />
        <WindowBattery color={colors.grey35} height={17} width={17} />
        <span>{date}</span>
        <span style={timeStyle}>{time}</span>
      </div>
      <div style={messageDesktopBody} data-test="web-push-desktop-message">
        <div style={messageDesktopContainer}>
          <div style={messageDesktopTitle}>
            {truncateText(
              title,
              selectedOS === OSWebPush.Windows
                ? WEB_PUSH_WINDOWS_TITLE_LIMIT
                : WEB_PUSH_NON_WINDOWS_TITLE_LIMIT,
            )}
          </div>
          <div style={messageDesktopNow}>now</div>
          <div style={messageDesktopContent}>
            <div>
              {truncateText(
                body,
                selectedOS === OSWebPush.Windows
                  ? WEB_PUSH_WINDOWS_BODY_LIMIT
                  : WEB_PUSH_NON_WINDOWS_BODY_LIMIT,
              )}
            </div>
          </div>
          {webPushData.icon && supportsImage && (
            <div
              style={{
                ...messageDesktopMedia,
                backgroundImage: `url(${webPushData.icon})`,
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};
